import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { ArticleSchema } from '@/components/feature/SeoSchema';

export default function BasetaoWarehouseGuide() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen">
      <Header />
      <ArticleSchema slug="basetao-warehouse-guide" />
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-900 transition-colors whitespace-nowrap">Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Warehouse & QC Guide</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase text-secondary-700 bg-secondary-100 rounded-full">Warehouse</span>
              <span className="text-xs text-foreground-500">Updated August 2026</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Basetao Warehouse Guide 2026 — Storage, QC Photos & Inspection
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              The <strong>Basetao warehouse</strong> is the backbone of the platform&apos;s service. Understanding how storage works, what QC photos reveal, and how to leverage the inspection process can dramatically improve your shopping experience and protect you from receiving unsatisfactory products.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">How the Basetao Warehouse Works</h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                When you place an order through Basetao, the purchased items are first shipped to the Basetao warehouse facility in China. Upon arrival, warehouse staff log each item into the system and take <strong>QC photos</strong> — quality control photographs that show the actual product you received. These photos are uploaded to your account, giving you the opportunity to inspect the items visually before they are packaged and shipped internationally.
              </p>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                Basetao offers free storage for a limited period — typically 30 to 90 days depending on the item type and current warehouse policies. This storage window is crucial because it allows you to accumulate multiple orders from different sellers and consolidate them into a single international shipment, which dramatically reduces per-item shipping costs. After the free storage period expires, nominal storage fees may apply.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Understanding QC Photos</h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                Quality control photographs are one of the most valuable features of the Basetao service. These images allow you to verify that the correct item was received, check for visible defects or damage, confirm sizes and colorways, and make an informed decision about whether to proceed with international shipping or request a return. Taking the time to carefully review QC photos has saved countless shoppers from receiving incorrect or damaged products.
              </p>
            </section>

            <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">FAQ</h2>
              <div className="space-y-5">
                {[
                  { q: 'How long does Basetao store items for free?', a: 'Basetao typically offers 30-90 days of free storage, though policies may vary. Check the current terms on the platform for the most accurate information.' },
                  { q: 'What if my QC photos show a damaged item?', a: 'If QC photos reveal damage or incorrect items, you can request a return or exchange through Basetao before the item ships internationally.' },
                ].map((faq, idx) => (
                  <div key={idx}><h3 className="text-base font-semibold text-foreground-900 mb-1.5">{faq.q}</h3><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-300 whitespace-nowrap"><i className="ri-home-4-line"></i>Back to Homepage</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-100 hover:bg-background-200 border border-background-300/60 rounded-full transition-colors duration-300 whitespace-nowrap">Visit Main Website<i className="ri-external-link-line"></i></a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}